import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const phases = [
  {
    phase: 'Phase 1',
    time: 'Now',
    status: 'live',
    title: 'Foundation Layer',
    items: ['NeuroOS-H live deployment', 'Proof-of-Inference (PoI)', 'Proof-of-Action (PoA)', 'Cerebro beta launch', 'Control Barrier Certificates'],
  },
  {
    phase: 'Phase 2',
    time: '6 Months',
    status: 'upcoming',
    title: 'Data Market Beta',
    items: ['nDATA-R token launch', 'Decentralized data marketplace', 'Privacy-preserving federated learning', 'DePIN network integration', 'Enterprise pilot programs'],
  },
  {
    phase: 'Phase 3',
    time: '12 Months',
    status: 'future',
    title: 'Royalty Streaming',
    items: ['iCTV royalty streaming live', 'Cross-OEM policy standardization', 'Enterprise compliance suite', 'Full Solana DePIN integration', 'Governance token launch'],
  },
  {
    phase: 'Phase 4',
    time: '18 Months',
    status: 'future',
    title: 'Regional Clusters',
    items: ['Regional compute clusters', 'Cross-chain expansion', 'Global Superbrain v1.0', 'Regulated markets entry (EU/US)', 'Insurance protocol partnership'],
  },
];

const statusConfig = {
  live: { color: '#00FF88', label: 'LIVE', bg: 'rgba(0, 255, 136, 0.08)' },
  upcoming: { color: '#00F0FF', label: 'UPCOMING', bg: 'rgba(0, 240, 255, 0.05)' },
  future: { color: '#444', label: 'PLANNED', bg: 'rgba(255,255,255,0.02)' },
};

export default function RoadmapSection() {
  const containerRef = useRef(null);
  const lineRef = useRef(null);
  const inView = useInView(containerRef, { once: true, margin: '-100px' });

  useEffect(() => {
    if (!lineRef.current || !containerRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          transformOrigin: 'top center',
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 65%',
            end: 'bottom 75%',
            scrub: 0.8,
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="roadmap"
      data-testid="roadmap-section"
      className="relative py-28 md:py-36 overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-4">
            Timeline
          </p>
          <h2
            className="font-display text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontFamily: "'Syne', sans-serif" }}
          >
            Roadmap
          </h2>
          <p className="text-[#555] mt-4 max-w-lg text-base leading-relaxed">
            A clear path from foundation to global deployment — built on milestones, not promises.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative" ref={containerRef}>
          {/* Vertical line */}
          <div
            className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px"
            style={{ background: 'rgba(255,255,255,0.06)', transform: 'translateX(-50%)' }}
          />
          {/* Animated overlay */}
          <div
            ref={lineRef}
            className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px"
            style={{
              background: 'linear-gradient(to bottom, #00F0FF, #4A90D9)',
              transform: 'translateX(-50%) scaleY(0)',
              transformOrigin: 'top',
              boxShadow: '0 0 8px rgba(0, 240, 255, 0.5)',
            }}
          />

          <div className="space-y-12 md:space-y-0">
            {phases.map((phase, i) => {
              const sc = statusConfig[phase.status];
              const isLeft = i % 2 === 0;

              return (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  className="relative flex items-start md:items-center"
                  style={{ marginBottom: i < phases.length - 1 ? '60px' : 0 }}
                  data-testid={`roadmap-phase-${i + 1}`}
                >
                  {/* Dot */}
                  <div
                    className="absolute left-[19px] md:left-1/2 w-4 h-4 rounded-full flex-shrink-0"
                    style={{
                      background: sc.color,
                      border: `2px solid ${sc.color}`,
                      boxShadow: `0 0 12px ${sc.color}60`,
                      transform: 'translate(-50%, 4px)',
                      zIndex: 10,
                    }}
                  />

                  {/* Content */}
                  <div
                    className={`ml-10 md:ml-0 md:w-[44%] ${
                      isLeft
                        ? 'md:mr-auto md:pr-10 md:text-right'
                        : 'md:ml-auto md:pl-10 md:text-left'
                    }`}
                  >
                    <div
                      className={`glass-card p-6 relative ${isLeft ? 'md:ml-auto' : ''}`}
                    >
                      {/* Status badge */}
                      <span
                        className="font-mono text-[10px] tracking-[0.15em] px-2 py-1 mb-4 inline-block"
                        style={{ color: sc.color, background: sc.bg, border: `1px solid ${sc.color}20` }}
                      >
                        {sc.label}
                      </span>

                      <div className="flex items-center gap-3 mb-1" style={{ justifyContent: isLeft ? 'flex-end' : 'flex-start' }}>
                        <span className="font-mono text-[#444] text-xs">{phase.phase}</span>
                        <span className="font-mono text-[#00F0FF] text-xs">{phase.time}</span>
                      </div>

                      <h3
                        className="font-display text-white font-semibold text-lg mb-4"
                        style={{ fontFamily: "'Syne', sans-serif" }}
                      >
                        {phase.title}
                      </h3>

                      <ul className="space-y-1.5" style={{ listStyleType: 'none', padding: 0 }}>
                        {phase.items.map((item) => (
                          <li
                            key={item}
                            className="text-xs text-[#666] flex items-center gap-2"
                            style={{ justifyContent: isLeft ? 'flex-end' : 'flex-start' }}
                          >
                            {isLeft && <span>{item}</span>}
                            <span
                              className="w-1 h-1 rounded-full flex-shrink-0"
                              style={{ background: sc.color, opacity: phase.status === 'future' ? 0.3 : 0.8 }}
                            />
                            {!isLeft && <span>{item}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
